// ===========================================
// 21v1 PROTOTYPE - HEAD-TO-HEAD DUEL MODE
// ===========================================
// Two players face each other left/right
// Dealer (shoe) remains top center
// Tug-of-war scoring: first to pull to 7 wins
// ===========================================

import { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// ===========================================
// TYPES
// ===========================================

interface Card {
  rank: string;
  suit: 'hearts' | 'diamonds' | 'clubs' | 'spades';
  faceUp: boolean;
}

interface PlayerState {
  cards: Card[];
  score: number;
  isBusted: boolean;
  isStanding: boolean;
  name: string;
}

type GamePhase = 'waiting' | 'dealing' | 'player_turn' | 'opponent_turn' | 'reveal' | 'scoring' | 'round_end';

// ===========================================
// CONSTANTS
// ===========================================

const SUIT_SYMBOLS: Record<string, string> = {
  hearts: '\u2665',
  diamonds: '\u2666',
  clubs: '\u2663',
  spades: '\u2660',
};

const SUIT_COLORS: Record<string, string> = {
  hearts: 'text-red-500',
  diamonds: 'text-red-500',
  clubs: 'text-gray-900',
  spades: 'text-gray-900',
};

// ===========================================
// HELPER FUNCTIONS
// ===========================================

const NUM_DECKS = 6;

const createShoe = (): Card[] => {
  const suits: Card['suit'][] = ['hearts', 'diamonds', 'clubs', 'spades'];
  const ranks = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
  const shoe: Card[] = [];

  // Create 6 decks (312 cards)
  for (let d = 0; d < NUM_DECKS; d++) {
    for (const suit of suits) {
      for (const rank of ranks) {
        shoe.push({ rank, suit, faceUp: true });
      }
    }
  }

  // Fisher-Yates shuffle
  for (let i = shoe.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shoe[i], shoe[j]] = [shoe[j], shoe[i]];
  }

  return shoe;
};

const getCardValue = (rank: string): number => {
  if (rank === 'A') return 11;
  if (['K', 'Q', 'J'].includes(rank)) return 10;
  return parseInt(rank, 10);
};

const calculateScore = (cards: Card[]): number => {
  let score = 0;
  let aces = 0;

  for (const card of cards) {
    if (!card.faceUp) continue;
    const value = getCardValue(card.rank);
    score += value;
    if (card.rank === 'A') aces++;
  }

  while (score > 21 && aces > 0) {
    score -= 10;
    aces--;
  }

  return score;
};

const calculateTrueScore = (cards: Card[]): number => {
  let score = 0;
  let aces = 0;

  for (const card of cards) {
    const value = getCardValue(card.rank);
    score += value;
    if (card.rank === 'A') aces++;
  }

  while (score > 21 && aces > 0) {
    score -= 10;
    aces--;
  }

  return score;
};

// Check for natural blackjack (21 with exactly 2 cards)
const isNaturalBlackjack = (cards: Card[]): boolean => {
  return cards.length === 2 && calculateTrueScore(cards) === 21;
};

// Calculate visible score (only face-up cards)
const calculateVisibleScore = (cards: Card[]): number => {
  let score = 0;
  let aces = 0;

  for (const card of cards) {
    if (!card.faceUp) continue; // Skip hole cards
    const value = getCardValue(card.rank);
    score += value;
    if (card.rank === 'A') aces++;
  }

  while (score > 21 && aces > 0) {
    score -= 10;
    aces--;
  }

  return score;
};

// Check if visibly busted (visible cards alone > 21)
const isVisiblyBusted = (cards: Card[]): boolean => {
  return calculateVisibleScore(cards) > 21;
};

// Calculate soft hand scores (returns both values if ace can be 1 or 11)
// Returns { low, high, isSoft } where isSoft means ace is being used as 11
const calculateSoftScores = (cards: Card[], onlyFaceUp: boolean = false): { low: number; high: number; isSoft: boolean } => {
  let score = 0;
  let aces = 0;

  for (const card of cards) {
    if (onlyFaceUp && !card.faceUp) continue;
    const value = getCardValue(card.rank);
    score += value;
    if (card.rank === 'A') aces++;
  }

  // If no aces or already busted, no soft hand
  if (aces === 0 || score <= 21) {
    // Reduce aces if needed
    while (score > 21 && aces > 0) {
      score -= 10;
      aces--;
    }
    return { low: score, high: score, isSoft: false };
  }

  // We have aces and score > 21, need to reduce
  let highScore = score;
  let lowScore = score;
  let acesUsedAsEleven = aces;

  // Reduce aces until we're at or below 21
  while (highScore > 21 && acesUsedAsEleven > 0) {
    highScore -= 10;
    acesUsedAsEleven--;
  }

  // Calculate lowest possible score (all aces as 1)
  lowScore = score - (aces * 10);

  // If high score is valid and we still have an ace as 11, it's a soft hand
  if (highScore <= 21 && acesUsedAsEleven > 0) {
    return { low: lowScore + (acesUsedAsEleven - 1) * 10, high: highScore, isSoft: true };
  }

  return { low: highScore, high: highScore, isSoft: false };
};

// Simpler version: check if hand is soft (has ace counting as 11 without busting)
const getSoftHandDisplay = (cards: Card[], onlyFaceUp: boolean = false): string | null => {
  let score = 0;
  let aces = 0;

  for (const card of cards) {
    if (onlyFaceUp && !card.faceUp) continue;
    const value = getCardValue(card.rank);
    score += value;
    if (card.rank === 'A') aces++;
  }

  if (aces === 0) return null;

  // Calculate score with all aces as 1
  const lowScore = score - (aces * 10);

  // Calculate score with one ace as 11 (rest as 1)
  const highScore = lowScore + 10;

  // Only show soft hand if high score is valid (<=21) and different from low
  if (highScore <= 21 && highScore !== lowScore) {
    return `${lowScore}/${highScore}`;
  }

  // If busted or no soft option, just return the final score
  while (score > 21 && aces > 0) {
    score -= 10;
    aces--;
  }

  return null; // No soft hand to display
};

// ===========================================
// CARD COMPONENT
// ===========================================

const CardDisplay: React.FC<{
  card: Card;
  index: number;
  isHole?: boolean;
  small?: boolean;
}> = ({ card, index, isHole = false, small = false }) => {
  const baseSize = small ? 'w-14 h-20' : 'w-20 h-28';
  const fontSize = small ? 'text-sm' : 'text-lg';

  if (!card.faceUp || isHole) {
    return (
      <motion.div
        initial={{ scale: 0, rotateY: 180 }}
        animate={{ scale: 1, rotateY: 0 }}
        transition={{ delay: index * 0.15, duration: 0.3 }}
        className={`${baseSize} rounded-lg bg-gradient-to-br from-blue-800 to-blue-900 border-2 border-blue-600 shadow-lg flex items-center justify-center`}
        style={{ marginLeft: index > 0 ? '-1rem' : 0 }}
      >
        <div className="w-3/4 h-3/4 rounded border border-blue-500 bg-blue-700/50 flex items-center justify-center">
          <span className="text-blue-300 text-2xl font-bold">?</span>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ scale: 0, rotateY: 180, x: -100 }}
      animate={{ scale: 1, rotateY: 0, x: 0 }}
      transition={{ delay: index * 0.15, duration: 0.3 }}
      className={`${baseSize} rounded-lg bg-white border border-gray-300 shadow-lg flex flex-col p-1 relative`}
      style={{ marginLeft: index > 0 ? '-1rem' : 0 }}
    >
      <div className={`${fontSize} font-bold ${SUIT_COLORS[card.suit]}`}>
        {card.rank}
        <span className="ml-0.5">{SUIT_SYMBOLS[card.suit]}</span>
      </div>
      <div className={`absolute bottom-1 right-1 ${fontSize} font-bold ${SUIT_COLORS[card.suit]} rotate-180`}>
        {card.rank}
        <span className="ml-0.5">{SUIT_SYMBOLS[card.suit]}</span>
      </div>
      <div className={`flex-1 flex items-center justify-center ${SUIT_COLORS[card.suit]}`}>
        <span className="text-4xl">{SUIT_SYMBOLS[card.suit]}</span>
      </div>
    </motion.div>
  );
};

// ===========================================
// TUG OF WAR SCORE BAR WITH INTEGRATED TIMER
// ===========================================

// Get timer color based on percentage remaining
const getTimerColor = (percent: number): string => {
  if (percent > 0.6) return 'rgb(34, 197, 94)';   // green-500
  if (percent > 0.35) return 'rgb(234, 179, 8)';  // yellow-500
  if (percent > 0.15) return 'rgb(249, 115, 22)'; // orange-500
  return 'rgb(239, 68, 68)';                       // red-500
};

// Get ambient background color based on match time remaining
const getAmbientBackground = (percent: number): string => {
  // Interpolate from cool (start) to warm (end)
  // Start: gray-900 with slight blue tint
  // End: gray-900 with red/warm tint
  if (percent > 0.7) return 'from-gray-900 via-gray-800 to-gray-900'; // Cool neutral
  if (percent > 0.4) return 'from-gray-900 via-amber-950/20 to-gray-900'; // Slight warmth
  if (percent > 0.15) return 'from-gray-900 via-orange-950/30 to-gray-900'; // Getting warm
  return 'from-gray-900 via-red-950/40 to-gray-900'; // Hot/urgent
};

// Format seconds as MM:SS
const formatTime = (seconds: number): string => {
  const mins = Math.floor(Math.max(0, seconds) / 60);
  const secs = Math.floor(Math.max(0, seconds) % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const TugOfWarBar: React.FC<{
  score: number; // -7 to +7, negative = player leading (toward their win)
  playerName: string;
  opponentName: string;
  activeSide: 'player' | 'opponent' | null;
  timeRemaining: number; // seconds remaining for decision
  maxTime: number; // max seconds for current decision phase
  matchTimeRemaining: number; // seconds remaining in match (out of 1260 = 21 min)
  matchMaxTime: number; // 1260 seconds = 21 minutes
}> = ({ score, playerName, opponentName, activeSide, timeRemaining, maxTime, matchTimeRemaining, matchMaxTime }) => {
  const positions = [-7, -6, -5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6, 7];
  const markerIndex = score + 7; // Convert -7..+7 to 0..14 index

  // Decision timer calculations
  const timerPercent = maxTime > 0 ? timeRemaining / maxTime : 0;
  const timerColor = getTimerColor(timerPercent);
  const timerWidth = `${timerPercent * 100}%`;

  // Match timer calculations (outer ring)
  const matchPercent = matchMaxTime > 0 ? matchTimeRemaining / matchMaxTime : 1;
  const matchColor = getTimerColor(matchPercent);
  const matchWidth = `${matchPercent * 100}%`;

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Labels */}
      <div className="flex justify-between mb-2 px-2">
        <div className="text-left">
          <span className="text-cyan-400 font-bold text-sm">{playerName}</span>
          <span className="text-gray-500 text-xs ml-2">← PULL</span>
        </div>
        <div className="text-right">
          <span className="text-gray-500 text-xs mr-2">PULL →</span>
          <span className="text-orange-400 font-bold text-sm">{opponentName}</span>
        </div>
      </div>

      {/* Outer container for both timer rings */}
      <div className="relative">
        {/* OUTER RING: Match timer (21 min) - Left side */}
        <div className="absolute -top-3 -bottom-3 -left-3 w-1/2 overflow-hidden rounded-l-full pointer-events-none">
          <div
            className="absolute top-0 bottom-0 left-0 transition-all duration-1000"
            style={{
              width: matchWidth,
              backgroundColor: matchColor,
              opacity: 0.6,
            }}
          />
        </div>

        {/* OUTER RING: Match timer (21 min) - Right side */}
        <div className="absolute -top-3 -bottom-3 -right-3 w-1/2 overflow-hidden rounded-r-full pointer-events-none">
          <div
            className="absolute top-0 bottom-0 right-0 transition-all duration-1000"
            style={{
              width: matchWidth,
              backgroundColor: matchColor,
              opacity: 0.6,
            }}
          />
        </div>

        {/* Black separator ring */}
        <div className="absolute -top-2 -bottom-2 -left-2 -right-2 rounded-full border-2 border-black pointer-events-none" />

        {/* INNER RING: Decision timer - Player side (left) */}
        <div className="absolute -top-1 -bottom-1 -left-1 w-1/2 overflow-hidden rounded-l-full pointer-events-none">
          <div
            className="absolute top-0 bottom-0 right-0 transition-all duration-100"
            style={{
              width: activeSide === 'player' ? timerWidth : '0%',
              backgroundColor: activeSide === 'player' ? timerColor : 'transparent',
              boxShadow: activeSide === 'player' ? `0 0 12px ${timerColor}` : 'none',
            }}
          />
        </div>

        {/* INNER RING: Decision timer - Opponent side (right) */}
        <div className="absolute -top-1 -bottom-1 -right-1 w-1/2 overflow-hidden rounded-r-full pointer-events-none">
          <div
            className="absolute top-0 bottom-0 left-0 transition-all duration-100"
            style={{
              width: activeSide === 'opponent' ? timerWidth : '0%',
              backgroundColor: activeSide === 'opponent' ? timerColor : 'transparent',
              boxShadow: activeSide === 'opponent' ? `0 0 12px ${timerColor}` : 'none',
            }}
          />
        </div>

        {/* Score Bar */}
        <div className="bg-gray-900 rounded-full p-1.5 border-4 border-gray-800">
          {/* Inner wrapper for precise marker positioning */}
          <div className="relative">
            <div className="flex items-center justify-between">
              {positions.map((pos) => {
                const isCenter = pos === 0;
                const isPlayerWin = pos === -7;
                const isOpponentWin = pos === 7;
                const isPlayerSide = pos < 0;

                return (
                  <div
                    key={pos}
                    className={`
                      relative flex items-center justify-center
                      ${isCenter ? 'w-10 h-10' : 'w-7 h-7'}
                      rounded-full text-xs font-bold
                      ${isCenter
                        ? 'bg-gray-700 text-gray-400 border-2 border-gray-500'
                        : isPlayerWin
                          ? 'bg-cyan-600 text-white border-2 border-cyan-400'
                          : isOpponentWin
                            ? 'bg-orange-600 text-white border-2 border-orange-400'
                            : isPlayerSide
                              ? 'bg-cyan-900/50 text-cyan-300 border border-cyan-700'
                              : 'bg-orange-900/50 text-orange-300 border border-orange-700'
                      }
                    `}
                  >
                    {Math.abs(pos)}
                  </div>
                );
              })}
            </div>

            {/* Marker - positioned relative to inner wrapper for precise alignment */}
            {/* Uses 3%-97% range to account for circle widths at edges */}
            <motion.div
              className="absolute top-0 bottom-0 flex items-center pointer-events-none"
              style={{ transform: 'translateX(-50%)' }}
              initial={false}
              animate={{
                left: `calc(3% + ${(markerIndex / 14) * 94}%)`,
              }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            >
              <div className="w-8 h-12 -mt-1 flex flex-col items-center">
                <div className="w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-yellow-400" />
                <div className="w-4 h-6 bg-yellow-400 rounded-b" />
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Win indicators */}
      <div className="flex justify-between mt-1 px-2">
        <span className="text-cyan-400 text-xs font-bold">WIN</span>
        <span className="text-gray-500 text-xs">First to 7 wins</span>
        <span className="text-orange-400 text-xs font-bold">WIN</span>
      </div>
    </div>
  );
};

// ===========================================
// PLAYER AREA COMPONENT
// ===========================================

const PlayerArea: React.FC<{
  player: PlayerState;
  side: 'left' | 'right';
  isActive: boolean;
  showHoleCard: boolean;
  isYou?: boolean;
  hasButton?: boolean;
}> = ({ player, side, isActive, showHoleCard, isYou = false, hasButton = false }) => {
  const visibleScore = showHoleCard
    ? calculateTrueScore(player.cards)
    : calculateScore(player.cards);

  const alignment = side === 'left' ? 'items-start' : 'items-end';
  const cardAlignment = side === 'left' ? 'justify-start' : 'justify-end';
  const bgGradient = side === 'left'
    ? 'from-cyan-900/20 to-transparent'
    : 'from-transparent to-orange-900/20';
  const borderColor = isActive
    ? (side === 'left' ? 'border-cyan-400' : 'border-orange-400')
    : 'border-gray-700';
  const glowColor = isActive
    ? (side === 'left' ? 'shadow-cyan-500/30' : 'shadow-orange-500/30')
    : '';

  // Button position: upper-right for left player, upper-left for right player
  const buttonPosition = side === 'left' ? '-top-3 -right-3' : '-top-3 -left-3';

  return (
    <div className={`flex-1 flex flex-col ${alignment} p-4`}>
      {/* Player info */}
      <div className={`
        relative mb-4 px-4 py-2 rounded-lg border-2 ${borderColor}
        bg-gradient-to-r ${bgGradient}
        ${isActive ? `shadow-lg ${glowColor}` : ''}
        transition-all duration-300
      `}>
        {/* Dealer Button */}
        <AnimatePresence>
          {hasButton && (
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0, rotate: 180 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              className={`absolute ${buttonPosition} w-8 h-8 rounded-full bg-yellow-500 border-2 border-yellow-300
                flex items-center justify-center font-black text-sm text-gray-900 shadow-lg shadow-yellow-500/50 z-10`}
            >
              D
            </motion.div>
          )}
        </AnimatePresence>

        <div className={`flex items-center gap-2 ${side === 'right' ? 'flex-row-reverse' : ''}`}>
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl
            ${side === 'left' ? 'bg-cyan-600' : 'bg-orange-600'}
          `}>
            {isYou ? '👤' : '🎭'}
          </div>
          <div className={side === 'right' ? 'text-right' : ''}>
            <div className={`font-bold ${side === 'left' ? 'text-cyan-400' : 'text-orange-400'}`}>
              {player.name}
            </div>
            <div className="text-gray-400 text-sm">
              {player.isStanding ? 'STANDING' : player.isBusted ? 'BUSTED!' : isActive ? 'Thinking...' : 'Waiting'}
            </div>
          </div>
        </div>
      </div>

      {/* Cards - separated into hole card and visible cards */}
      <div className={`flex ${cardAlignment} gap-3 mb-4`}>
        {/* Hole Card (first card - hidden from opponent) */}
        {player.cards.length > 0 && (
          <div className={`relative ${isYou ? 'bg-gray-800/30 border border-dashed border-gray-600 rounded-lg p-1' : ''}`}>
            {isYou && (
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-1.5 py-0.5 bg-gray-800 text-gray-500 text-[10px] rounded whitespace-nowrap">
                HIDDEN
              </div>
            )}
            <CardDisplay
              card={player.cards[0]}
              index={0}
              isHole={!showHoleCard && !isYou}
            />
          </div>
        )}

        {/* Visible Cards (all cards after the first) */}
        {player.cards.length > 1 && (
          <div className={`relative flex ${isYou ? 'bg-cyan-900/20 border border-dashed border-cyan-700/50 rounded-lg p-1' : ''}`}>
            {isYou && (
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-1.5 py-0.5 bg-gray-800 text-cyan-500 text-[10px] rounded whitespace-nowrap">
                VISIBLE
              </div>
            )}
            {player.cards.slice(1).map((card, idx) => (
              <CardDisplay
                key={idx + 1}
                card={card}
                index={idx}
                isHole={false}
              />
            ))}
          </div>
        )}
      </div>

      {/* Score */}
      <div className={`
        px-4 py-2 rounded-lg
        ${player.isBusted
          ? 'bg-red-900/50 border border-red-500'
          : 'bg-gray-800/50 border border-gray-600'
        }
      `}>
        <span className="text-gray-400 text-sm mr-2">Score:</span>
        <span className={`text-2xl font-bold ${player.isBusted ? 'text-red-400' : 'text-white'}`}>
          {(() => {
            // For opponent without hole card revealed, only show visible cards
            const onlyFaceUp = !showHoleCard && !isYou;
            const softDisplay = getSoftHandDisplay(player.cards, onlyFaceUp);

            if (softDisplay) {
              return softDisplay;
            }
            return visibleScore;
          })()}
          {!showHoleCard && !isYou && player.cards.length > 0 && (
            <span className="text-gray-500 text-lg ml-1">+?</span>
          )}
        </span>
      </div>
    </div>
  );
};

// ===========================================
// SHOE / DEALER AREA
// ===========================================

const ShoeArea: React.FC<{ cardsRemaining: number }> = ({ cardsRemaining }) => {
  return (
    <div className="flex flex-col items-center">
      {/* Shoe visual */}
      <div className="relative">
        {/* Stacked cards effect */}
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="absolute w-16 h-24 rounded-lg bg-gradient-to-br from-blue-800 to-blue-900 border border-blue-600"
            style={{
              top: i * 2,
              left: i * 2,
              zIndex: 4 - i,
            }}
          />
        ))}
        <div className="relative w-16 h-24 rounded-lg bg-gradient-to-br from-blue-800 to-blue-900 border-2 border-blue-500 flex items-center justify-center z-10">
          <span className="text-blue-300 text-xs font-bold">{cardsRemaining}</span>
        </div>
      </div>
      <span className="text-gray-500 text-xs mt-2">SHOE</span>
    </div>
  );
};

// ===========================================
// ACTION BUTTONS
// ===========================================

const ActionButtons: React.FC<{
  onHit: () => void;
  onStand: () => void;
  disabled: boolean;
  isYourTurn: boolean;
}> = ({ onHit, onStand, disabled, isYourTurn }) => {
  return (
    <div className="flex gap-4">
      <motion.button
        whileHover={{ scale: disabled ? 1 : 1.05 }}
        whileTap={{ scale: disabled ? 1 : 0.95 }}
        onClick={onHit}
        disabled={disabled}
        className={`
          px-8 py-3 rounded-lg font-bold text-lg
          ${disabled
            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
            : 'bg-green-600 hover:bg-green-500 text-white shadow-lg shadow-green-500/30'
          }
          transition-colors
        `}
      >
        HIT
      </motion.button>
      <motion.button
        whileHover={{ scale: disabled ? 1 : 1.05 }}
        whileTap={{ scale: disabled ? 1 : 0.95 }}
        onClick={onStand}
        disabled={disabled}
        className={`
          px-8 py-3 rounded-lg font-bold text-lg
          ${disabled
            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
            : 'bg-yellow-600 hover:bg-yellow-500 text-white shadow-lg shadow-yellow-500/30'
          }
          transition-colors
        `}
      >
        STAND
      </motion.button>
    </div>
  );
};

// ===========================================
// ROUND RESULT OVERLAY
// ===========================================

const RoundResult: React.FC<{
  result: 'win' | 'lose' | 'push' | null;
  onContinue: () => void;
}> = ({ result, onContinue }) => {
  if (!result) return null;

  const config = {
    win: { text: 'YOU WIN!', subtext: 'PULL!', color: 'text-green-400', bg: 'from-green-900/90' },
    lose: { text: 'YOU LOSE', subtext: 'Opponent pulls...', color: 'text-red-400', bg: 'from-red-900/90' },
    push: { text: 'PUSH', subtext: 'No movement', color: 'text-gray-400', bg: 'from-gray-800/90' },
  };

  const { text, subtext, color, bg } = config[result];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={`absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-b ${bg} to-transparent z-50`}
      >
        <motion.div
          initial={{ scale: 0, rotate: -10 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 200 }}
          className="text-center"
        >
          <div className={`text-6xl font-black ${color} mb-2`}>{text}</div>
          <div className="text-2xl text-gray-300 mb-8">{subtext}</div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onContinue}
            className="px-8 py-3 bg-white text-gray-900 rounded-lg font-bold text-lg shadow-lg"
          >
            Next Round
          </motion.button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ===========================================
// MAIN COMPONENT
// ===========================================

const TwentyOneV1Page: React.FC = () => {
  // Game state
  const [deck, setDeck] = useState<Card[]>(() => createShoe());
  const [player, setPlayer] = useState<PlayerState>({
    cards: [],
    score: 0,
    isBusted: false,
    isStanding: false,
    name: 'YOU',
  });
  const [opponent, setOpponent] = useState<PlayerState>({
    cards: [],
    score: 0,
    isBusted: false,
    isStanding: false,
    name: 'OPPONENT',
  });
  const [phase, setPhase] = useState<GamePhase>('waiting');
  const [tugScore, setTugScore] = useState(0); // -7 to +7
  const [roundResult, setRoundResult] = useState<'win' | 'lose' | 'push' | null>(null);
  const [buttonHolder, setButtonHolder] = useState<'player' | 'opponent'>('player');
  const [matchWinner, setMatchWinner] = useState<'player' | 'opponent' | null>(null);

  // Ref to track button holder for avoiding stale closures
  const buttonHolderRef = useRef(buttonHolder);
  useEffect(() => { buttonHolderRef.current = buttonHolder; }, [buttonHolder]);

  // Timer state
  const [matchStartTime, setMatchStartTime] = useState<number | null>(null);
  const [gameTimeElapsed, setGameTimeElapsed] = useState(0); // seconds
  const [decisionTimeRemaining, setDecisionTimeRemaining] = useState(21);
  const [maxDecisionTime, setMaxDecisionTime] = useState(21); // Track max for this turn
  const [isSuddenDeath, setIsSuddenDeath] = useState(false);

  // Refs to avoid stale closures in setTimeout callbacks
  const deckRef = useRef(deck);
  const playerRef = useRef(player);
  const opponentRef = useRef(opponent);
  const phaseRef = useRef(phase);

  // Keep refs in sync
  useEffect(() => { deckRef.current = deck; }, [deck]);
  useEffect(() => { playerRef.current = player; }, [player]);
  useEffect(() => { opponentRef.current = opponent; }, [opponent]);
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  // Game clock - tracks total match time
  useEffect(() => {
    if (!matchStartTime || matchWinner) return;

    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - matchStartTime) / 1000);
      setGameTimeElapsed(elapsed);

      // Check for 21 minute limit
      if (elapsed >= 21 * 60 && !isSuddenDeath) {
        // Time's up! Determine winner or go to sudden death
        const currentScore = tugScore;
        if (currentScore < 0) {
          // Player is ahead
          setMatchWinner('player');
        } else if (currentScore > 0) {
          // Opponent is ahead
          setMatchWinner('opponent');
        } else {
          // Tied - sudden death
          setIsSuddenDeath(true);
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [matchStartTime, matchWinner, tugScore, isSuddenDeath]);

  // Decision timer - counts down during active turns
  // Reset timer when turn changes
  useEffect(() => {
    if (phase !== 'player_turn' && phase !== 'opponent_turn') {
      return;
    }

    // Calculate max time for this phase
    const calcMaxTime = () => {
      if (isSuddenDeath) return 30;
      const minutes = gameTimeElapsed / 60;
      if (minutes >= 18) return 3;
      if (minutes >= 15) return 6;
      if (minutes >= 12) return 9;
      if (minutes >= 9) return 12;
      if (minutes >= 6) return 15;
      if (minutes >= 3) return 18;
      return 21;
    };

    const maxTime = calcMaxTime();
    setMaxDecisionTime(maxTime);
    setDecisionTimeRemaining(maxTime);

    let timeoutHandled = false;
    const TICK_MS = 50; // Smooth 50ms updates
    const DECREMENT = TICK_MS / 1000; // Decrement per tick

    const interval = setInterval(() => {
      setDecisionTimeRemaining(prev => {
        const newTime = prev - DECREMENT;
        if (newTime <= 0 && !timeoutHandled) {
          timeoutHandled = true;
          // Time's up - handle timeout in next tick to avoid state updater side effects
          setTimeout(() => {
            if (phaseRef.current === 'player_turn') {
              setPlayer(p => ({ ...p, isStanding: true }));
              if (opponentRef.current.isStanding) {
                setTimeout(() => endRound(), 500);
              } else {
                setPhase('opponent_turn');
                setTimeout(() => simulateOpponentTurn(), 500);
              }
            }
          }, 0);
          return 0;
        }
        return Math.max(0, newTime);
      });
    }, TICK_MS);

    return () => clearInterval(interval);
  }, [phase, isSuddenDeath]); // Only reset when phase or sudden death changes

  // Draw a card from deck
  const drawCard = useCallback((faceUp: boolean = true): Card | null => {
    if (deck.length === 0) return null;
    const card = { ...deck[0], faceUp };
    setDeck(prev => prev.slice(1));
    return card;
  }, [deck]);

  // Start a new round
  const startRound = useCallback(() => {
    // Toggle button at START of round (except first round)
    // This ensures the toggle and usage happen synchronously
    let whoGoesFirst: 'player' | 'opponent';
    if (!matchStartTime) {
      // First round of match - player with button goes first
      whoGoesFirst = buttonHolderRef.current;
      setMatchStartTime(Date.now());
      setGameTimeElapsed(0);
      setIsSuddenDeath(false);
    } else {
      // Subsequent rounds - toggle button, then that player goes first
      const newButtonHolder = buttonHolderRef.current === 'player' ? 'opponent' : 'player';
      setButtonHolder(newButtonHolder);
      buttonHolderRef.current = newButtonHolder; // Update ref immediately
      whoGoesFirst = newButtonHolder;
    }

    const newDeck = createShoe();
    setDeck(newDeck);

    // Deal initial cards: each player gets 2 cards (first is hole card)
    const playerCards: Card[] = [
      { ...newDeck[0], faceUp: true }, // Player's hole card (visible to them)
      { ...newDeck[2], faceUp: true },
    ];
    const opponentCards: Card[] = [
      { ...newDeck[1], faceUp: false }, // Opponent's hole card (hidden)
      { ...newDeck[3], faceUp: true },
    ];

    setDeck(newDeck.slice(4));

    setPlayer({
      cards: playerCards,
      score: calculateTrueScore(playerCards),
      isBusted: false,
      isStanding: false,
      name: 'YOU',
    });

    setOpponent({
      cards: opponentCards,
      score: calculateScore(opponentCards),
      isBusted: false,
      isStanding: false,
      name: 'OPPONENT',
    });

    setRoundResult(null);

    // Check for natural blackjacks
    const playerHasBJ = isNaturalBlackjack(playerCards);
    const opponentHasBJ = isNaturalBlackjack(opponentCards);

    if (playerHasBJ || opponentHasBJ) {
      // Reveal opponent's hole card for blackjack resolution
      setOpponent(prev => ({
        ...prev,
        cards: prev.cards.map(c => ({ ...c, faceUp: true })),
      }));

      setPhase('reveal');

      // Delay to show the cards, then resolve
      setTimeout(() => {
        let result: 'win' | 'lose' | 'push';
        if (playerHasBJ && opponentHasBJ) {
          result = 'push'; // Both have BJ
        } else if (playerHasBJ) {
          result = 'win'; // Player BJ wins
        } else {
          result = 'lose'; // Opponent BJ wins
        }

        // Update tug score
        setTugScore(prev => {
          let newScore = prev;
          if (result === 'win') newScore = prev - 1;
          if (result === 'lose') newScore = prev + 1;

          if (newScore <= -7) setMatchWinner('player');
          else if (newScore >= 7) setMatchWinner('opponent');

          return Math.max(-7, Math.min(7, newScore));
        });

        setRoundResult(result);
        setPhase('round_end');
        // Button will be toggled at START of next round
      }, 1500);

      return; // Don't start normal turn flow
    }

    // Normal round - no blackjacks
    setPhase(whoGoesFirst === 'player' ? 'player_turn' : 'opponent_turn');

    // If opponent goes first, trigger their turn after a delay
    if (whoGoesFirst === 'opponent') {
      setTimeout(() => simulateOpponentTurn(), 1500);
    }
  }, [matchStartTime]);

  // Player hits
  const handleHit = useCallback(() => {
    if (phase !== 'player_turn') return;

    const newCard = drawCard(true);
    if (!newCard) return;

    const newCards = [...player.cards, newCard];
    const newScore = calculateTrueScore(newCards);
    const isBusted = newScore > 21;

    // Update both state AND ref immediately to avoid stale reads in simulateOpponentTurn
    const newPlayerState = {
      ...player,
      cards: newCards,
      score: newScore,
      isBusted,
    };
    playerRef.current = newPlayerState; // Immediate ref update
    setPlayer(newPlayerState);

    // Check if player visibly busted (visible cards > 21)
    const visiblyBusted = isVisiblyBusted(newCards);

    if (visiblyBusted) {
      // VISIBLE BUST = instant loss for player
      setTimeout(() => endRound(), 500);
    } else if (!opponent.isStanding) {
      // Opponent still playing - alternate turns
      setPhase('opponent_turn');
      setTimeout(() => simulateOpponentTurn(), 1000);
    }
    // If opponent is already standing but player isn't visibly busted,
    // player can continue (stays on player_turn) - they'll click HIT or STAND again
  }, [phase, player, opponent, drawCard]);

  // Player stands
  const handleStand = useCallback(() => {
    if (phase !== 'player_turn') return;

    // Update both state AND ref immediately to avoid stale reads in simulateOpponentTurn
    const newPlayerState = { ...playerRef.current, isStanding: true };
    playerRef.current = newPlayerState;
    setPlayer(newPlayerState);

    if (opponent.isStanding) {
      // Both standing, reveal and score
      endRound();
    } else {
      // Opponent's turn
      setPhase('opponent_turn');
      setTimeout(() => simulateOpponentTurn(), 1000);
    }
  }, [phase, opponent]);

  // Simulate opponent's turn (simple AI) - uses refs to avoid stale closures
  const simulateOpponentTurn = useCallback(() => {
    const currentDeck = deckRef.current;
    const currentPlayer = playerRef.current;
    const currentOpponent = opponentRef.current;

    const trueScore = calculateTrueScore(currentOpponent.cards);

    // Simple strategy: hit until 17+
    if (trueScore < 17 && !currentOpponent.isBusted) {
      // Hit
      const newCard = currentDeck[0];
      if (!newCard) return;

      const newCards = [...currentOpponent.cards, { ...newCard, faceUp: true }];
      const newScore = calculateTrueScore(newCards);
      const isBusted = newScore > 21;

      setDeck(d => d.slice(1));

      // Update both state AND ref immediately to avoid stale reads on recursive calls
      const newOpponentState = {
        ...currentOpponent,
        cards: newCards,
        score: newScore,
        isBusted,
      };
      opponentRef.current = newOpponentState; // Immediate ref update
      setOpponent(newOpponentState);

      // Check if opponent visibly busted (visible cards > 21)
      const visiblyBusted = isVisiblyBusted(newCards);

      // After ONE hit, alternate back to player (unless visibly busted or player standing/busted)
      if (visiblyBusted) {
        // VISIBLE BUST = instant loss for opponent, no bluff possible
        setTimeout(() => endRound(), 500);
      } else if (currentPlayer.isStanding || currentPlayer.isBusted) {
        // Player already standing OR busted - opponent can keep going
        // (When player is busted, they're done making meaningful decisions)
        if (newScore >= 17 || isBusted) {
          // Opponent done (reached 17+ or hidden bust = bluffing as stand)
          setOpponent(prev => ({ ...prev, isStanding: true }));
          setTimeout(() => endRound(), 500);
        } else {
          // Opponent keeps hitting since player is standing/busted
          setTimeout(() => simulateOpponentTurn(), 1000);
        }
      } else {
        // ALTERNATE TURNS: Player's turn now
        setPhase('player_turn');
      }
    } else {
      // Already at 17+ or busted - stand
      setOpponent(prev => ({ ...prev, isStanding: true }));

      if (currentPlayer.isStanding || currentPlayer.isBusted) {
        // Both done (standing or busted), end round
        setTimeout(() => endRound(), 500);
      } else {
        // Player's turn
        setPhase('player_turn');
      }
    }
  }, []);

  // End round and determine winner - uses refs to avoid stale closures
  const endRound = useCallback(() => {
    setPhase('reveal');

    // Reveal opponent's hole card
    setOpponent(prev => ({
      ...prev,
      cards: prev.cards.map(c => ({ ...c, faceUp: true })),
    }));

    setTimeout(() => {
      // Use refs for current state
      const currentPlayer = playerRef.current;
      const currentOpponent = opponentRef.current;

      const playerScore = calculateTrueScore(currentPlayer.cards);
      const opponentScore = calculateTrueScore(currentOpponent.cards);
      const playerBusted = playerScore > 21;
      const opponentBusted = opponentScore > 21;

      let result: 'win' | 'lose' | 'push';

      if (playerBusted && opponentBusted) {
        result = 'push';
      } else if (playerBusted) {
        result = 'lose';
      } else if (opponentBusted) {
        result = 'win';
      } else if (playerScore > opponentScore) {
        result = 'win';
      } else if (opponentScore > playerScore) {
        result = 'lose';
      } else {
        result = 'push';
      }

      // Update tug score
      setTugScore(prev => {
        let newScore = prev;
        if (result === 'win') newScore = prev - 1;
        if (result === 'lose') newScore = prev + 1;

        // Check for match win
        if (newScore <= -7) {
          setMatchWinner('player');
        } else if (newScore >= 7) {
          setMatchWinner('opponent');
        }

        return Math.max(-7, Math.min(7, newScore));
      });

      setRoundResult(result);
      setPhase('round_end');
      // Button will be toggled at START of next round
    }, 1000);
  }, []);

  // Continue to next round
  const handleContinue = useCallback(() => {
    if (matchWinner) {
      // Reset match for new game
      setTugScore(0);
      setMatchWinner(null);
      setMatchStartTime(null);
      setGameTimeElapsed(0);
      setIsSuddenDeath(false);
    }
    startRound();
  }, [matchWinner, startRound]);

  // Reset match
  const handleNewMatch = useCallback(() => {
    setTugScore(0);
    setMatchWinner(null);
    setButtonHolder('player');
    setPhase('waiting');
    setMatchStartTime(null);
    setGameTimeElapsed(0);
    setIsSuddenDeath(false);
    setDecisionTimeRemaining(21);
    setMaxDecisionTime(21);
    setPlayer({
      cards: [],
      score: 0,
      isBusted: false,
      isStanding: false,
      name: 'YOU',
    });
    setOpponent({
      cards: [],
      score: 0,
      isBusted: false,
      isStanding: false,
      name: 'OPPONENT',
    });
  }, []);

  const isYourTurn = phase === 'player_turn';
  const showHoleCards = phase === 'reveal' || phase === 'round_end';

  // Match timer calculations
  const MATCH_DURATION = 21 * 60; // 21 minutes in seconds
  const matchTimeRemaining = Math.max(0, MATCH_DURATION - gameTimeElapsed);
  const matchPercent = matchTimeRemaining / MATCH_DURATION;
  const ambientBg = getAmbientBackground(matchPercent);

  return (
    <div className={`min-h-screen bg-gradient-to-b ${ambientBg} text-white overflow-hidden transition-colors duration-[5000ms]`}>
      {/* Header */}
      <div className="text-center py-4 border-b border-gray-700">
        <h1 className="text-3xl font-black">
          <span className="text-yellow-400">21</span>
          <span className="text-gray-400">v</span>
          <span className="text-yellow-400">1</span>
        </h1>
        <p className="text-gray-500 text-sm">No dealer. No mercy.</p>
      </div>

      {/* Dual Timer Display */}
      <div className="max-w-2xl mx-auto mt-4 px-4">
        <div className="flex justify-between items-center bg-gray-800/50 rounded-lg px-4 py-2 border border-gray-700">
          {/* Left: Player's move timer */}
          <div className={`text-sm font-mono ${phase === 'player_turn' ? 'text-cyan-400' : 'text-gray-500'}`}>
            <span className="text-gray-500 text-xs mr-1">MOVE:</span>
            <span className="font-bold">{phase === 'player_turn' ? formatTime(decisionTimeRemaining) : '--:--'}</span>
          </div>

          {/* Center: Match timer */}
          <div className={`text-lg font-mono font-bold ${matchPercent < 0.15 ? 'text-red-400' : matchPercent < 0.4 ? 'text-orange-400' : 'text-white'}`}>
            <span className="text-gray-500 text-xs mr-1">MATCH:</span>
            <span>{formatTime(matchTimeRemaining)}</span>
            {isSuddenDeath && <span className="ml-2 text-red-500 text-xs animate-pulse">SUDDEN DEATH</span>}
          </div>

          {/* Right: Opponent's move timer */}
          <div className={`text-sm font-mono ${phase === 'opponent_turn' ? 'text-orange-400' : 'text-gray-500'}`}>
            <span className="font-bold">{phase === 'opponent_turn' ? formatTime(decisionTimeRemaining) : '--:--'}</span>
            <span className="text-gray-500 text-xs ml-1">:MOVE</span>
          </div>
        </div>
      </div>

      {/* Tug of War Score */}
      <div className="py-6 px-4">
        <TugOfWarBar
          score={tugScore}
          playerName="YOU"
          opponentName="OPPONENT"
          activeSide={phase === 'player_turn' ? 'player' : phase === 'opponent_turn' ? 'opponent' : null}
          timeRemaining={decisionTimeRemaining}
          maxTime={maxDecisionTime}
          matchTimeRemaining={matchTimeRemaining}
          matchMaxTime={MATCH_DURATION}
        />
      </div>

      {/* Main Game Area */}
      <div className="relative flex-1 px-4">
        {/* Shoe at top center */}
        <div className="flex justify-center mb-8">
          <ShoeArea cardsRemaining={deck.length} />
        </div>

        {/* Players facing each other - constrained to bar width */}
        <div className="flex items-stretch min-h-[300px] max-w-2xl mx-auto">
          {/* Left Player (YOU) */}
          <PlayerArea
            player={player}
            side="left"
            isActive={phase === 'player_turn'}
            showHoleCard={true}
            isYou={true}
            hasButton={buttonHolder === 'player'}
          />

          {/* VS Divider */}
          <div className="flex flex-col items-center justify-center px-8">
            <div className="text-4xl font-black text-gray-600">VS</div>
          </div>

          {/* Right Player (OPPONENT) */}
          <PlayerArea
            player={opponent}
            side="right"
            isActive={phase === 'opponent_turn'}
            showHoleCard={showHoleCards}
            isYou={false}
            hasButton={buttonHolder === 'opponent'}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center mt-8">
          {phase === 'waiting' ? (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={startRound}
              className="px-12 py-4 bg-yellow-500 hover:bg-yellow-400 text-gray-900 rounded-lg font-bold text-xl shadow-lg shadow-yellow-500/30"
            >
              DEAL
            </motion.button>
          ) : (
            <ActionButtons
              onHit={handleHit}
              onStand={handleStand}
              disabled={!isYourTurn || player.isStanding}
              isYourTurn={isYourTurn}
            />
          )}
        </div>

        {/* Turn Indicator */}
        {phase !== 'waiting' && phase !== 'reveal' && phase !== 'round_end' && (
          <div className="text-center mt-4">
            <span className={`text-lg font-bold ${isYourTurn ? 'text-cyan-400' : 'text-orange-400'}`}>
              {isYourTurn ? "Your turn — Hit or Stand" : "Opponent is thinking..."}
            </span>
          </div>
        )}

        {/* Round Result Overlay */}
        <RoundResult result={roundResult} onContinue={handleContinue} />

        {/* Match Winner Overlay */}
        {matchWinner && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 flex flex-col items-center justify-center bg-black/90 z-50"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200 }}
              className="text-center"
            >
              <div className={`text-7xl font-black mb-4 ${matchWinner === 'player' ? 'text-cyan-400' : 'text-orange-400'}`}>
                {matchWinner === 'player' ? 'VICTORY!' : 'DEFEAT'}
              </div>
              <div className="text-2xl text-gray-300 mb-8">
                {matchWinner === 'player' ? 'You pulled to 7!' : 'Opponent pulled to 7'}
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleNewMatch}
                className="px-8 py-3 bg-yellow-500 text-gray-900 rounded-lg font-bold text-lg shadow-lg"
              >
                New Match
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </div>

      {/* Footer */}
      <div className="text-center py-4 text-gray-600 text-sm">
        <span className="mr-4">Prototype v0.1</span>
        <button
          onClick={handleNewMatch}
          className="text-gray-500 hover:text-gray-300 underline"
        >
          Reset Match
        </button>
      </div>
    </div>
  );
};

export default TwentyOneV1Page;
